import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { LineChart } from "@mui/x-charts/LineChart";
import { apiService } from "../../services/api";
import { brand } from "../../theme/themePrimitives";

function AreaGradient({ color, id }: { color: string; id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

interface TrendData {
  months: string[];
  avgGrades: (number | null)[];
  callCounts: number[];
  totalCalls: number;
  trend: number | null;
  trendPct: number | null;
}

interface Props {
  overallAvg: number | null;
  salespersonId?: string;
}

export default function CallsPerformanceChart({ overallAvg, salespersonId }: Props) {
  const [data, setData] = useState<TrendData | null>(null);

  useEffect(() => {
    const fetch = salespersonId
      ? apiService.getSalespersonMonthlyTrends(salespersonId)
      : apiService.getCallMonthlyTrends();
    fetch.then((res) => {
      if (res.success && res.data) {
        setData(res.data);
      }
    });
  }, [salespersonId]);

  const months = data?.months ?? ["", "", "", "", "", ""];
  // Replace nulls with 0 so the line stays continuous even when only one month has data
  const avgGrades = (data?.avgGrades ?? [null, null, null, null, null, null]).map(
    (v) => (v === null ? 0 : v),
  );
  const trendPct = data?.trendPct ?? null;

  let trendLabel = "";
  let trendColor: "success" | "error" | "default" = "default";
  if (trendPct !== null) {
    trendLabel = `${trendPct >= 0 ? "+" : ""}${trendPct}%`;
    trendColor = trendPct >= 0 ? "success" : "error";
  }

  const lineColor = brand[500];

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Average Call Grade
        </Typography>
        <Stack sx={{ justifyContent: "space-between" }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              {overallAvg !== null ? overallAvg.toFixed(1) : "—"}
            </Typography>
            {trendLabel && (
              <Tooltip
                title="Change in average call grade vs previous month"
                arrow
              >
                <Chip size="small" color={trendColor} label={trendLabel} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Monthly average grade
          </Typography>
        </Stack>
        <LineChart
          colors={[lineColor]}
          xAxis={[
            {
              scaleType: "point",
              data: months,
              height: 24,
            },
          ]}
          yAxis={[
            {
              min: 0,
              max: 10,
              tickNumber: 6,
              width: 50,
            },
          ]}
          series={[
            {
              id: "avgGrade",
              label: "Avg Grade",
              showMark: true,
              curve: "linear",
              area: true,
              data: avgGrades,
              valueFormatter: (v) => (v !== null ? v.toFixed(1) : "No data"),
            },
          ]}
          height={250}
          margin={{ left: 0, right: 50, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={{
            "& .MuiAreaElement-series-avgGrade": {
              fill: "url('#avgGrade')",
            },
            "& .MuiMarkElement-root": {
              opacity: 0,
            },
            "& .MuiMarkElement-root.MuiMarkElement-highlighted": {
              opacity: 1,
            },
            "& .MuiChartsAxis-line": {
              stroke: "#9ca3af",
            },
            "& .MuiChartsAxis-tick": {
              stroke: "#9ca3af",
            },
            "& .MuiChartsAxis-tickLabel": {
              fill: "#6b7280 !important",
              fontWeight: 500,
            },
            "& .MuiChartsGrid-line": {
              stroke: "#e5e7eb",
              strokeDasharray: "4 2",
              strokeWidth: 0.8,
            },
          }}
          hideLegend
        >
          <AreaGradient color={lineColor} id="avgGrade" />
        </LineChart>
      </CardContent>
    </Card>
  );
}
